import CONFIG from '../constants/config'
import {AuthService} from '../service/auth.services'
import {StorageService} from '../service/storage.services'
import {
  EErrorCodes,
  ErrorHelper,
  isAuthError,
  TFetchResponse,
} from '../types/app.type'
import {TRefreshTokenResponse} from '../types/auth.type'

const CONTENT_TYPE_JSON = 'application/json'

function get<Response>(url: string) {
  return fetchGeneric<Response>(url, {
    method: 'GET',
  })
}

function post<Body, Response>(url: string, body: Body | FormData) {
  if (body instanceof FormData) {
    return fetchGeneric<Response>(url, {
      method: 'POST',
      body,
    })
  } else {
    // A bodyless POST must not send Content-Type: application/json — an empty
    // body with that header makes Fastify reject the request with a 400.
    return fetchGeneric<Response>(url, {
      method: 'POST',
      ...(body !== undefined && {
        body: JSON.stringify(body),
        headers: {'Content-Type': CONTENT_TYPE_JSON},
      }),
    })
  }
}

function patch<Body, Response>(
  url: string,
  body?: Body,
  headers: Record<string, string> = {},
) {
  return fetchGeneric<Response>(url, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : {},
    headers: {
      ...(body && {'Content-Type': CONTENT_TYPE_JSON}),
      ...headers,
    },
  })
}

function put<Body, Response>(url: string, body: Body) {
  return fetchGeneric<Response>(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': CONTENT_TYPE_JSON,
    },
  })
}

function del(url: string) {
  return fetchGeneric<void>(url, {
    method: 'DELETE',
  })
}

function delWithBody<Body, Response>(url: string, body: Body) {
  return fetchGeneric<Response>(url, {
    method: 'DELETE',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': CONTENT_TYPE_JSON,
    },
  })
}

const fetcher = {
  get,
  post,
  patch,
  put,
  del,
  delWithBody,
}

export default fetcher

async function fetchGeneric<T>(
  url: string,
  config: any = {},
): Promise<TFetchResponse<T>> {
  const authAccess = StorageService.getAuthAccess()

  if (authAccess && isProtectedUrl(url)) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${authAccess.accessToken}`,
    }
  }

  let response: Response
  try {
    response = await fetch(`${CONFIG.apiUrl}${url}`, config)
  } catch (error) {
    // fetch() only throws on network failures (server down, DNS, CORS preflight,
    // offline). HTTP errors are NOT thrown — they come through response.ok.
    // Wrap as a typed ErrorHelper so callers can distinguish network vs auth.
    throw new ErrorHelper({
      statusCode: 0,
      message: error instanceof Error ? error.message : 'Network error',
      code: EErrorCodes.NETWORK_ERROR,
      data: null,
      error: 'NetworkError',
    })
  }
  if (!response.ok) {
    const res = await handleResponseError<T>(response, url, config)
    if (res) {
      return res
    }
    return {
      status: 204,
      ok: response.ok,
      statusText: response.statusText,
    }
  } else {
    if (response.status === 204) {
      return {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
      }
    }
    const data: T = await response.json()

    return {
      status: response.status,
      ok: response.ok,
      statusText: response.statusText,
      data,
    }
  }
}

const isProtectedUrl = (url: string) => {
  // Important : utiliser startsWith ou des patterns exacts pour éviter les faux positifs
  // Ex: '/api/authorizedConflict' ne doit PAS matcher '/api/auth'
  return !(url.startsWith('/api/auth/') || url === '/api/auth')
}

const handleResponseError = async <T>(
  response: Response,
  url: string,
  config: any = {},
): Promise<TFetchResponse<T> | void> => {
  const dataError = await response.json()
  if (response.status === 401) {
    const authAccess = StorageService.getAuthAccess()
    if (
      dataError.code === EErrorCodes.INVALID_TOKEN ||
      dataError.code === EErrorCodes.EXPIRED_TOKEN
    ) {
      if (!authAccess?.rememberMe) {
        return AuthService.logout()
      }
      return await handleRefreshToken(
        url,
        config,
        authAccess?.refreshToken as string,
      )
    } else if (dataError.code === EErrorCodes.MISSING_TOKEN) {
      return AuthService.logout()
    } else {
      throw new ErrorHelper(dataError)
    }
  } else {
    throw new ErrorHelper(dataError)
  }
}

// Singleton promise to deduplicate concurrent refresh token requests.
// If multiple requests fail with 401 simultaneously, only one refresh call is made.
let refreshTokenPromise: Promise<TRefreshTokenResponse | undefined> | null =
  null

const handleRefreshToken = async <T>(
  url: string,
  config: any,
  refreshToken: string,
): Promise<TFetchResponse<T>> => {
  try {
    if (!refreshTokenPromise) {
      refreshTokenPromise = AuthService.refreshAccessToken(
        refreshToken,
      ).finally(() => {
        refreshTokenPromise = null
      })
    }

    const newTokens = await refreshTokenPromise

    if (newTokens === undefined || newTokens === null) {
      throw new ErrorHelper({
        statusCode: 401,
        message: 'Unauthorized',
        code: EErrorCodes.INVALID_TOKEN,
        data: null,
        error: 'Unauthorized',
      })
    }
    StorageService.updateAuthTokens(
      newTokens.accessToken,
      newTokens.refreshToken,
    )
    return fetchGeneric(url, config)
  } catch (error: any) {
    // Only logout when the refresh genuinely failed for an auth reason.
    // Network errors / 5xx must NOT clear the session — the user stays logged in
    // and the original caller can retry once the API is reachable again.
    if (isAuthError(error)) {
      AuthService.logout()
    }
    throw new ErrorHelper({
      statusCode: error.statusCode,
      message: error.message,
      code: error.code,
      data: error.data,
      error: error.error,
    })
  }
}
